/*
 * TÚNEL REAL · scrub de frames por scroll — yanipaper
 * ===================================================
 * Reemplaza el túnel placeholder de Three.js por FOOTAGE FOTO-REAL: una secuencia de frames
 * (data-center ciberpunk, primera persona) que se "scrubbea" con el scroll. El video es un LOOP
 * generado (docs/muestra_una_animación_en_bucle.mp4), extraído a public/tunnel-frames-real/ + manifest.
 *
 * Decisiones:
 *   - Canvas 2D sobre el mismo #cinema (no WebGL): dibuja el frame vigente en modo COVER con leve
 *     overscan, así llena la pantalla en cualquier aspecto sin barras.
 *   - El footage actual (Pixabay, 1080p) viene LIMPIO, sin marca de agua → no lleva máscara/viñeta.
 *     (El video viejo traía un ✦ en la esquina inf-der que se tapaba con una viñeta; ya no aplica.)
 *   - Precarga progresiva: dibuja el primer frame apenas llega (revelado temprano) y sigue cargando
 *     el resto en segundo plano; drawProgress omite frames aún no cargados sin romper.
 *
 * NO decide el scroll ni el ciclo de vida: main.js le pasa el progreso 0→1 y lo monta/desmonta
 * dentro de mountCinema (gate/intro, Lenis, ScrollTrigger, reveals y constelación DOM siguen ahí).
 */

const DIR = `${import.meta.env.BASE_URL}tunnel-frames-real/`;

// Overscan: recorta ~2% de cada borde (zoom 1.02) para garantizar cobertura sin costuras en cualquier
// aspecto. El footage nuevo es 1080p limpio; se mantiene mínimo para no sacrificar nitidez.
const OVERSCAN = 1.02;
const BG = '#04040a'; // sustrato casi-negro (coincide con el poster/canon)

export function createTunnelScrub({ canvas }) {
  const ctx = canvas.getContext('2d', { alpha: false });
  let man = null;
  let N = 0;
  let PAD = 4;
  let imgs = [];        // imgs[1..N]
  let cur = -1;         // último frame dibujado
  let want = 1;         // frame que el scroll pide ahora mismo (puede no estar cargado aún)
  let dpr = 1;

  const url = (i) => `${DIR}${man.prefix}${String(i).padStart(PAD, '0')}.${man.ext}`;

  function fit() {
    dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(window.innerWidth * dpr);
    canvas.height = Math.round(window.innerHeight * dpr);
    // OJO: asignar canvas.width/height RESETEA el contexto → re-activar DESPUÉS la interpolación de
    // ALTA calidad (bicúbica). El footage es 720p y se estira; esto suaviza el escalado (menos pixeleo).
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    // re-dibuja el frame vigente al nuevo tamaño
    if (cur > 0) drawIndex(cur, true);
  }

  // Dibuja la imagen cubriendo el lienzo (cover) con leve overscan centrado.
  function drawCover(img) {
    const cw = canvas.width, ch = canvas.height;
    const iw = img.naturalWidth, ih = img.naturalHeight;
    const s = Math.max(cw / iw, ch / ih) * OVERSCAN;
    const w = iw * s, h = ih * s;
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, cw, ch);
    ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
  }

  function drawIndex(idx, force = false) {
    if (!man) return;
    if (idx === cur && !force) return;
    const img = imgs[idx];
    if (!img || !img.complete || !img.naturalWidth) return; // aún no cargado: no rompe, espera
    cur = idx;
    drawCover(img);
  }

  // Descarga UN frame y lo deja decodificado en imgs[i]. Usa img.decode() para decodificar FUERA del
  // hilo principal (evita el jank de la primera vez que se dibuja durante el scrub). No lanza: si un
  // frame falla, deja imgs[i] = null y drawIndex lo salta sin romper.
  async function loadOne(i) {
    const im = new Image();
    im.decoding = 'async';
    im.src = url(i);
    try {
      await im.decode();
    } catch {
      // decode() rechaza en error de red/formato; también si el navegador es viejo. Fallback: si al
      // menos cargó (naturalWidth), lo aceptamos; si no, queda null.
      if (!im.naturalWidth) { imgs[i] = null; return; }
    }
    imgs[i] = im;
    // Si este es justo el frame que el scroll está pidiendo, píntalo ya (llegó "tarde").
    if (i === want && i !== cur) drawIndex(i);
  }

  // Carga manifest + precarga los frames con CONCURRENCIA ACOTADA (no 240 peticiones de golpe, que
  // saturan una conexión lenta y retrasan el primer pintado). Resuelve apenas el PRIMER frame está
  // listo (revelado temprano); el resto sigue en segundo plano, EN ORDEN, con un pool de CONC obreros.
  // onProgress(0..1) opcional. En LAN es instantáneo; en conexión lenta el túnel aparece pronto y se
  // "rellena" hacia adelante mientras Héctor lee/scrollea.
  async function load(onProgress) {
    man = await fetch(DIR + 'manifest.json').then((r) => r.json());
    N = man.count;
    PAD = man.pad || 4;
    imgs = new Array(N + 1);

    // 1) Primer frame primero → revelado temprano; el gate/intro ya no espera 30 MB.
    await loadOne(1);
    drawIndex(1, true);
    let loaded = 1;
    if (onProgress) onProgress(loaded / N);

    // 2) Resto en segundo plano: pool de CONC obreros consumiendo 2..N en orden.
    const CONC = 6;
    let next = 2;
    const worker = async () => {
      while (next <= N) {
        const i = next++;
        await loadOne(i);
        loaded++;
        if (onProgress) onProgress(loaded / N);
      }
    };
    // No se espera (no bloquea el arranque); corre en segundo plano.
    Promise.all(Array.from({ length: CONC }, worker));

    return { count: N };
  }

  // Mapea progreso de scroll 0→1 al frame vigente [1..N]. Guarda `want` para que, si el frame pedido
  // aún no cargó, se pinte en cuanto llegue (drawIndex mantiene mientras tanto el último dibujado).
  function drawProgress(p) {
    if (!man) return;
    const t = p < 0 ? 0 : p > 1 ? 1 : p;
    const idx = Math.min(N, Math.max(1, Math.round(t * (N - 1)) + 1));
    want = idx;
    drawIndex(idx);
  }

  function resize() { fit(); }

  fit();

  return {
    load,
    drawProgress,
    resize,
    get count() { return N; },
    get index() { return cur; },
  };
}
